<script setup>
import { provide, defineProps, computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
const route = useRoute();
import { useWindowSize } from '@vueuse/core';
import SubscribePopup from '@/components/SubscribePopup.vue';
import CookieBanner from '@/components/CookieBanner.vue';
import { POLICY_URL, SUBSCRIBE_URL } from '@/data/links';
const { width } = useWindowSize();
const isMenuOpen = ref(false);

watch(isMenuOpen, (val) => {
  document.body.classList.toggle('_lock', val);
});

// Пункты, общие для меню в шапке и в подвале.
const commonLinks = [
  { label: 'О компании', to: { name: 'home', hash: '#about' } },
  { label: 'Бренды', to: { name: 'home', hash: '#brands' } },
  { label: 'Какао-академия', to: '/kakao-academy', activeClass: 'header__nav-link_active' },
]

// В шапке к ним добавляется якорь к подвалу с контактами.
const headerLinks = computed(() => [
  ...commonLinks,
  { label: 'Контакты', to: { name: route.name, hash: '#contacts' } },
])

// В подвале вместо него ссылка на главную: контакты напечатаны рядом,
// в соседней колонке, вести к ним из того же подвала незачем.
const footerLinks = [
  { label: 'Главная', to: '/' },
  ...commonLinks,
]

defineProps({
  initialData: {
    type: Object,
    default: () => ({})
  },
});

const DLEvent = (eventName, ParameterName1 = 'click', ParameterValue1 = 'click', ParameterName2 = null, ParameterValue2 = null) => {
  const baseEvent = {
    event: 'DLevent',
    eventName: eventName
  };

  let eventData;

  if (ParameterName2 && ParameterValue2) {
    eventData = {
      ParameterName1: ParameterName1,
      ParameterValue1: ParameterValue1,
      ParameterName2: ParameterName2,
      ParameterValue2: ParameterValue2
    };
  } else {
    eventData = {
      ParameterName1: ParameterName1,
      ParameterValue1: ParameterValue1,
    };
  }
  dataLayer.push({...baseEvent, ...eventData});
}

provide('DLEvent', DLEvent);

</script>

<template>
  <div :class="['main', `_${route.name}`]">
    <div class="main__limiter">
      <div class="header">
        <router-link to="/" class="header__logo">
          <img src="@/assets/img/logo.svg" alt="" />
        </router-link>
        <div class="header__nav">
          <router-link
            v-for="link in headerLinks"
            :key="link.label"
            :to="link.to"
            class="header__nav-link"
            :active-class="link.activeClass"
            :exact-active-class="link.exactActiveClass"
          >{{ link.label }}</router-link>
        </div>
        <a class="header__subscribe-button" :href="SUBSCRIBE_URL" target="_blank">Подписаться</a>
        <a href="" class="header__menu-button" @click.prevent="isMenuOpen = true">
          <svg width="21" height="15" viewBox="0 0 21 15" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 1H21M0 7.5H21M0 14H21" stroke="#642000" stroke-width="2"/>
          </svg>
        </a>
        <div :class="['header__mobile-menu', { '_open': isMenuOpen }]">
          <div class="header__mobile-menu-inner">
            <a href="" class="header__mobile-menu-close" @click.prevent="isMenuOpen = false">
              <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.710938 15.5564L15.5602 0.707168M0.718004 0.707108L15.5672 15.5563" stroke="#642000" stroke-width="2"/>
              </svg>
            </a>
            <img src="@/assets/img/menu-title.svg" alt="" class="header__mobile-menu-title"/>
            <div class="header__mobile-nav">
              <router-link
                v-for="link in headerLinks"
                :key="link.label"
                :to="link.to"
                class="header__mobile-nav-link"
                @click="isMenuOpen = false"
              >{{ link.label }}</router-link>
            </div>
            <div class="header__mobile-menu-bottom">
              <a :href="POLICY_URL" target="_blank">Политика конфиденциальности</a>
            </div>
          </div>
        </div>
      </div>
      <router-view />
      <div class="footer" id="contacts">
        <img src="@/assets/img/footer-bg.svg" alt="" class="footer__bg" v-if="width > 767"/>
        <img src="@/assets/img/footer-bg-mobile.png" alt="" class="footer__bg" v-else/>
        <div class="footer__inner">
          <img src="@/assets/img/logo.svg" alt="" class="footer__logo"/>
          <div class="footer__columns">
            <div class="footer__contacts">
              <h4>Звоните и пишите:</h4>
              <div class="footer__phone">
                <a href="tel:88002007200" class="footer__phone">
                  <span>RU</span>
                  8 800 200 7 200
                </a>
              </div>
              <div class="footer__phone">
                <a href="tel:81080020007200">
                  <span>BY</span>
                  8 10 800 2000 72 00
                </a>
              </div>
              <div class="footer__mail">
                <a href="https://max.ru/nestle_support_bot" target="_blank">
                  <img src="@/assets/img/icon-max.svg" alt="" />
                  @nestle_support_bot
                </a>
                <span></span>
                <a href="mailto:contact@info.nestle.ru">
                  <img src="@/assets/img/icon-mail.svg" alt="" />
                  contact@info.nestle.ru
                </a>
              </div>
              <p>Другие способы связаться с нами на <a href="https://www.nestle.ru/info" target="_blank">www.nestle.ru</a></p>
            </div>
            <div class="footer__menu">
              <p v-for="link in footerLinks" :key="link.label">
                <router-link :to="link.to">{{ link.label }}</router-link>
              </p>
              <a :href="SUBSCRIBE_URL" class="footer__subscribe-button" target="_blank">Подписаться</a>
            </div>
          </div>
          <div class="footer__copy">
            <h4>Компания Nestlé, 2026&nbsp;г. Все права защищены.</h4>
            <p>® Владелец товарных знаков: Société des Produits Nestlé S.A. (Швейцария).</p>
            <p>® Владелец товарного знака «Россия» - Щедрая Душа!®: ООО «Нестле Россия»</p>
          </div>
          <div class="footer__bottom-links">
            <a :href="POLICY_URL" target="_blank">Политика конфиденциальности</a>
          </div>
        </div>
      </div>
    </div>
    <SubscribePopup />
    <CookieBanner />
  </div>
</template>
